import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export enum BatchStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
}

export interface BatchItem {
  _id: string;
  name: string;
  description?: string;
  tenant: string;
  courses: any[];
  liveClasses: any[];
  students: any[];
  startDate?: string | Date;
  endDate?: string | Date;
  status: BatchStatus;
  createdBy?: any;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export interface BatchesResponse {
  docs: BatchItem[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
}

export interface BulkEnrollPayload {
  targetType: 'COURSE' | 'LIVE_CLASS';
  targetId: string;
  expiresInDays?: number;
}

export interface BulkEnrollResult {
  enrolledCount: number;
  alreadyEnrolledCount: number;
  totalStudents: number;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class BatchService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.host}/api/v1/batches`;

  getBatches(
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
  ): Observable<BatchesResponse | BatchItem[]> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    if (status?.trim()) {
      params = params.set('status', status.trim());
    }

    return this.http.get<BatchesResponse | BatchItem[]>(this.baseUrl, {
      params,
    });
  }

  getBatchById(id: string): Observable<BatchItem> {
    return this.http.get<BatchItem>(`${this.baseUrl}/${id}`);
  }

  createBatch(data: Partial<BatchItem>): Observable<BatchItem> {
    return this.http.post<BatchItem>(this.baseUrl, data);
  }

  updateBatch(id: string, data: Partial<BatchItem>): Observable<BatchItem> {
    return this.http.put<BatchItem>(`${this.baseUrl}/${id}`, data);
  }

  deleteBatch(id: string): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ success: boolean; message: string }>(
      `${this.baseUrl}/${id}`,
    );
  }

  addStudents(batchId: string, studentIds: string[]): Observable<BatchItem> {
    return this.http.post<BatchItem>(`${this.baseUrl}/${batchId}/students`, {
      studentIds,
    });
  }

  removeStudent(batchId: string, studentId: string): Observable<BatchItem> {
    return this.http.delete<BatchItem>(
      `${this.baseUrl}/${batchId}/students/${studentId}`,
    );
  }

  addCourses(batchId: string, courseIds: string[]): Observable<BatchItem> {
    return this.http.post<BatchItem>(`${this.baseUrl}/${batchId}/courses`, {
      courseIds,
    });
  }

  removeCourse(batchId: string, courseId: string): Observable<BatchItem> {
    return this.http.delete<BatchItem>(
      `${this.baseUrl}/${batchId}/courses/${courseId}`,
    );
  }

  addLiveClasses(
    batchId: string,
    liveClassIds: string[],
  ): Observable<BatchItem> {
    return this.http.post<BatchItem>(
      `${this.baseUrl}/${batchId}/live-classes`,
      { liveClassIds },
    );
  }

  removeLiveClass(batchId: string, classId: string): Observable<BatchItem> {
    return this.http.delete<BatchItem>(
      `${this.baseUrl}/${batchId}/live-classes/${classId}`,
    );
  }

  bulkEnroll(
    batchId: string,
    payload: BulkEnrollPayload,
  ): Observable<BulkEnrollResult> {
    return this.http.post<BulkEnrollResult>(
      `${this.baseUrl}/${batchId}/bulk-enroll`,
      payload,
    );
  }
}
